/**
 * password-checklist.js — 密碼即時檢核清單（共用元件）
 *
 * register / reset-password / member-edit 三處共用一支邏輯（避免三份重複）。
 * 由 auth-forms.js（register + reset）與 member-edit.js（變更密碼）import 並呼叫
 * initPasswordChecklist()，本身不 auto-init（無對應 DOM 即不會被呼叫）。
 *
 * 依 dawa 規格（2026-06-04「密碼即時檢核清單 .pw-checklist」）：
 *   - 清單三項硬性規則（即時兩態 pending / met，無紅態）：
 *       len   值長度 ≥ 8
 *       alpha 含英文字母 /[A-Za-z]/
 *       num   含數字 /\d/
 *   - 確認欄「一致」為獨立指示 .pw-match（綠色「與密碼一致」），非併入清單；
 *     不一致走既有 .form-field__error（紅字 role=alert，由 host 端維持）。二者互斥永不同框。
 *   - 顯示時機：focus（含 input 與 toggle 鈕）或首次輸入才展開，展開後不收合；
 *     mount 時若 input 已有值直接顯示。
 *   - a11y：清單容器 role=list；每項 role=checkbox + aria-checked + aria-disabled（唯讀）；
 *     不對清單即時播報，改在 input aria-describedby 指向 visually-hidden 彙總 span（aria-live=polite），
 *     僅於「全達成 / 未全達成」臨界點翻轉一次，debounce 250ms 防連續輸入多次播報。
 *   - 動態：展開 grid-template-rows 0fr→1fr（CSS 控）；單項 icon opacity 對切 + label 換色（CSS 控）；
 *     reduced-motion 退靜態由 CSS @media 處理（本 JS 不讀 matchMedia）。
 *
 * 慣例沿用：escapeHtml 不需要（本檔不把使用者輸入插回 innerHTML，只切 class / aria-checked）；
 *   DOM 由 buildChecklistDom 一次性 innerHTML 注入靜態 markup（無使用者資料），與 member-replies.js 同風。
 *
 * 用法：
 *   const pc = initPasswordChecklist({ pwd, confirm, confirmError, onChange });
 *   // host gate 取 pc.isValid()（三項皆 met && 一致）；pwd 變動 host 端會收到 onChange。
 *   // host 自行清值後呼叫 pc.refresh() 重算狀態（成功 submit 清空密碼欄後用）。
 *
 * 掛載位置自動偵測：input 若被 .member-pwd-field（含顯示/隱藏 toggle 的相對定位殼）包住，
 *   清單 / 一致指示掛在該殼「之後」（不塞進相對定位殼內，避免破排版）；否則掛在 input 之後
 *   （register 密碼欄無殼，直接子於 .form-field）。可用 mountAfter / confirmMountAfter 顯式覆寫。
 */

const RULES = [
  { key: 'len', label: '至少 8 個字元', test: (v) => v.length >= 8 },
  { key: 'alpha', label: '包含英文字母', test: (v) => /[A-Za-z]/.test(v) },
  { key: 'num', label: '包含數字', test: (v) => /\d/.test(v) },
];

const ARIA_LIVE_DEBOUNCE = 250;
const SUMMARY_ALL_MET = '密碼已符合所有條件';

/* pending：空心圓（中性）。met：實心 teal 圓底 + 白勾。兩 SVG 疊放，由 CSS opacity 對切。 */
const ICON_PENDING_SVG =
  '<svg class="pw-checklist__icon-svg pw-checklist__icon-svg--pending" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<circle cx="10" cy="10" r="7.25" stroke="currentColor" stroke-width="1.5" /></svg>';
const ICON_MET_SVG =
  '<svg class="pw-checklist__icon-svg pw-checklist__icon-svg--met" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<circle cx="10" cy="10" r="8" fill="currentColor" />' +
  '<path class="pw-check-mark" d="M6.5 10.25 9 12.75l4.5-4.5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" /></svg>';

const MATCH_ICON_SVG =
  '<svg class="pw-match__icon-svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<circle cx="10" cy="10" r="8" fill="currentColor" />' +
  '<path class="pw-check-mark" d="M6.5 10.25 9 12.75l4.5-4.5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" /></svg>';

let uid = 0;
function nextId(prefix) {
  uid += 1;
  return `${prefix}-${uid}`;
}

/* 掛載錨點：input 若在 .member-pwd-field（相對定位 toggle 殼）內，回該殼；否則回 input 本身。 */
function mountAnchorFor(input) {
  const wrap = input.closest('.member-pwd-field');
  return wrap || input;
}

/* ---------- DOM 構建 ----------
 * 結構：.pw-checklist（展開動畫殼，grid-template-rows 0fr↔1fr）
 *         > .pw-checklist__inner（overflow:hidden 被裁切內容，flex column 排三項）
 *             > .pw-checklist__item ×3
 * 0fr↔1fr 收合需「單一可裁切子層」承載全部內容（沿用 admin-data.css 收合列既有機制）。 */
function buildChecklistDom() {
  const list = document.createElement('div');
  list.className = 'pw-checklist';
  list.hidden = true;

  const itemsHtml = RULES.map(
    (rule) =>
      `<p class="pw-checklist__item" role="checkbox" aria-checked="false" aria-disabled="true" data-rule="${rule.key}">` +
      `<span class="pw-checklist__icon" aria-hidden="true">${ICON_PENDING_SVG}${ICON_MET_SVG}</span>` +
      `<span class="pw-checklist__label">${rule.label}</span>` +
      '</p>'
  ).join('');

  // role=list 放在 inner（語意群=三項），__inner 為清單容器；外層 .pw-checklist 僅作動畫殼。
  list.innerHTML =
    `<div class="pw-checklist__inner" role="list" aria-label="密碼需符合下列條件">${itemsHtml}</div>`;

  return list;
}

/** 確認欄「與密碼一致」綠色指示（實心 teal 圓 + 白勾），aria-live 一致時才顯。 */
function buildMatchDom() {
  const match = document.createElement('p');
  match.className = 'pw-match';
  match.setAttribute('aria-live', 'polite');
  match.hidden = true;
  match.innerHTML =
    `<span class="pw-match__icon" aria-hidden="true">${MATCH_ICON_SVG}</span>` +
    '<span class="pw-match__label">與密碼一致</span>';
  return match;
}

/* visually-hidden 彙總狀態 span（input aria-describedby 指向它；本專案隱藏 class 為 .visually-hidden）。 */
function buildSummaryDom(id) {
  const span = document.createElement('span');
  span.className = 'visually-hidden';
  span.id = id;
  span.setAttribute('aria-live', 'polite');
  return span;
}

/* ---------- 主工廠 ---------- */
export function initPasswordChecklist(opts) {
  const {
    pwd,
    confirm = null,
    confirmError = null,
    mountAfter = null,
    confirmMountAfter = null,
    onChange = null,
  } = opts || {};
  if (!pwd) return null;

  // 清單掛在 mountAfter 之後（未給則自動偵測：.member-pwd-field 殼後 / 否則 input 後）。
  const pwdAnchor = mountAfter || mountAnchorFor(pwd);
  const list = buildChecklistDom();
  pwdAnchor.insertAdjacentElement('afterend', list);
  const items = Array.from(list.querySelectorAll('.pw-checklist__item'));

  // 彙總狀態 span 接到 pwd aria-describedby（取代原靜態 hint 的描述）。
  const summaryId = nextId('pw-summary');
  const summary = buildSummaryDom(summaryId);
  list.insertAdjacentElement('afterend', summary);
  const prevDescribed = pwd.getAttribute('aria-describedby');
  pwd.setAttribute(
    'aria-describedby',
    prevDescribed ? `${prevDescribed} ${summaryId}` : summaryId
  );

  // 確認欄一致指示（僅 confirm 存在時建立）。掛在確認欄之後；若確認欄在 .member-pwd-field
  // 殼內則掛殼後（會落在既有 __error 之前，二者互斥不同框）。
  let match = null;
  if (confirm) {
    match = buildMatchDom();
    const confirmAnchor = confirmMountAfter || mountAnchorFor(confirm);
    confirmAnchor.insertAdjacentElement('afterend', match);
  }

  let expanded = false;
  let lastAllMet = null; // null = 尚未播報過；防初始即播
  let summaryTimer = 0;

  /** 展開清單（一次性：展開後不再收合，符合 dawa 規格「展開後不收合」）。 */
  function expand() {
    if (expanded) return;
    expanded = true;
    list.hidden = false;
  }

  /** 臨界播報：僅在「全達成 ⇄ 未全達成」翻轉時更新 aria-live span 一次，debounce 防連續輸入狂念。 */
  function announceSummary(allMet) {
    if (lastAllMet === allMet) return;
    lastAllMet = allMet;
    window.clearTimeout(summaryTimer);
    summaryTimer = window.setTimeout(() => {
      // 僅在臨界翻轉播一次：全達成播肯定句；回未達成清空（不狂念逐項）。
      summary.textContent = allMet ? SUMMARY_ALL_MET : '';
    }, ARIA_LIVE_DEBOUNCE);
  }

  /** 重算三項規則 + 一致指示的視覺/aria 狀態，回 { allMet, isMatch }。每次輸入呼叫。 */
  function evaluate() {
    const value = pwd.value;
    let allMet = true;
    items.forEach((item, i) => {
      const met = RULES[i].test(value);
      if (!met) allMet = false;
      item.classList.toggle('is-met', met);
      item.setAttribute('aria-checked', met ? 'true' : 'false');
    });

    // 一致指示：一致且密碼非空 → 顯綠色 .pw-match；其餘隱藏（不一致由 host 走 __error）。
    let isMatch = false;
    if (match && confirm) {
      isMatch = value !== '' && confirm.value === value;
      match.hidden = !isMatch;
    }

    announceSummary(allMet);
    return { allMet, isMatch };
  }

  /** 「整體有效」（host gate 取此判 submit 開關）：三項皆 met；若有確認欄則須非空且一致。 */
  function isValid() {
    const value = pwd.value;
    const allMet = RULES.every((r) => r.test(value));
    if (!confirm) return allMet;
    return allMet && value !== '' && confirm.value === value;
  }

  /** input 事件處理：展開 → 重算 → 通知 host（host 據此 syncGate 重算 submit disabled）。 */
  function handleInput() {
    expand();
    evaluate();
    if (typeof onChange === 'function') onChange();
  }

  // focus（含 toggle 鈕等同欄內任何 focusin）或首次輸入即展開。
  pwd.addEventListener('focus', expand);
  pwd.addEventListener('input', handleInput);
  if (confirm) {
    confirm.addEventListener('focus', expand);
    confirm.addEventListener('input', handleInput);
  }

  // mount 時若已有值（編輯態理論上為空，罕見）→ 直接顯示並評估。
  if (pwd.value) {
    expand();
    evaluate();
  }

  return {
    isValid,
    // host 清空密碼欄後呼叫：重置狀態為初始（不收合，符合「展開後不收合」，但清回 pending）。
    refresh() {
      lastAllMet = null;
      window.clearTimeout(summaryTimer);
      summary.textContent = '';
      evaluate();
    },
  };
}
